import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Scissors, Package, XCircle, ArrowRight, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

import { PageHeader } from "@/components/layout/page-header";
import { StatusBadge } from "@/components/data/status-badge";
import { Link } from "wouter";
import { cn } from "@/lib/utils";

type ImportType = "mangaras" | "colheitas";
type ImportError = { linha: number; erro: string };
/** O que o servidor devolveu em cada passo — só para o resumo do fim. */
type Resultado = { sucesso: number; total: number; erros: number };

/**
 * Importar é FLUXO, não navegação.
 *
 * Estas duas etapas eram `PageTabs` até a Onda 4, e aba diz "escolha uma das
 * duas" — quando a verdade é "faça esta, depois aquela": a colheita referencia a
 * fita que o mangará criou, então importar colheitas antes é produzir registro
 * sem ciclo. O passo numerado diz a ordem; a aba escondia.
 */
const PASSOS: { id: ImportType; n: number; label: string; icone: typeof Scissors }[] = [
  { id: "mangaras", n: 1, label: "Mangarás", icone: Scissors },
  { id: "colheitas", n: 2, label: "Colheitas", icone: Package },
];

export default function ImportarPage() {
  const { toast } = useToast();
  // `fim` é o estado de conclusão: o resumo do que entrou e a saída da tela.
  const [passo, setPasso] = useState<ImportType | "fim">("mangaras");
  const [concluidos, setConcluidos] = useState<Record<string, boolean>>({});
  const [resultados, setResultados] = useState<Record<string, Resultado>>({});
  const [uploadingMangaras, setUploadingMangaras] = useState(false);
  const [uploadingColheitas, setUploadingColheitas] = useState(false);
  const [fileMangaras, setFileMangaras] = useState<File | null>(null);
  const [fileColheitas, setFileColheitas] = useState<File | null>(null);
  const [errosMangaras, setErrosMangaras] = useState<ImportError[]>([]);
  const [errosColheitas, setErrosColheitas] = useState<ImportError[]>([]);
  // O recálculo do rateio roda uma vez no fim do import e NÃO derruba a importação quando
  // falha (as colheitas já estão gravadas). Sem este aviso, o request mais propenso a
  // estourar era justamente o que devolvia sucesso limpo e mandava o erro só pro Sentry.
  const [rateioDesatualizado, setRateioDesatualizado] = useState(false);

  const handleFileChange = (type: ImportType, e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.name.endsWith(".xlsx") || selectedFile.name.endsWith(".xls")) {
        if (type === "mangaras") {
          setFileMangaras(selectedFile);
          setErrosMangaras([]);
        } else {
          setFileColheitas(selectedFile);
          setErrosColheitas([]);
        }
      } else {
        toast({
          title: "Arquivo inválido",
          description: "Por favor, selecione um arquivo Excel (.xlsx ou .xls)",
          variant: "destructive",
        });
      }
    }
  };

  /** Do passo concluído para o seguinte — e do último para o resumo. */
  const avancar = (de: ImportType) => setPasso(de === "mangaras" ? "colheitas" : "fim");

  const handleUpload = async (type: ImportType) => {
    const file = type === "mangaras" ? fileMangaras : fileColheitas;
    if (!file) return;

    const setUploading = type === "mangaras" ? setUploadingMangaras : setUploadingColheitas;
    const setFile = type === "mangaras" ? setFileMangaras : setFileColheitas;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(`/api/import/${type}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: formData,
      });

      const result = await response.json();

      if (response.ok) {
        const nomeImportacao = type === "mangaras" ? "Mangarás" : "Colheitas";
        
        // Save errors to state for display
        if (type === "mangaras") {
          setErrosMangaras(result.erros || []);
        } else {
          setErrosColheitas(result.erros || []);
          // `rateioRecalculado` só existe na importação de colheitas.
          setRateioDesatualizado(result.rateioRecalculado === false);
        }

        toast({
          title: "Importação concluída!",
          description: `${nomeImportacao}: ${result.sucesso} de ${result.total} registros importados com sucesso. ${result.erros.length > 0 ? `${result.erros.length} erros encontrados.` : ""}`,
        });

        setFile(null);

        // O passo fica concluído; o que muda é SE avança sozinho. Com linha
        // recusada, ficar é o certo: avançar tiraria da tela justamente o
        // relatório de erro que a pessoa precisa ler para corrigir a planilha.
        const nErros = (result.erros || []).length;
        setResultados(prev => ({
          ...prev,
          [type]: { sucesso: result.sucesso, total: result.total, erros: nErros },
        }));
        setConcluidos(prev => ({ ...prev, [type]: true }));
        if (nErros === 0) avancar(type);
      } else {
        toast({
          title: "Erro na importação",
          description: result.error || "Ocorreu um erro ao importar os dados.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Erro na importação",
        description: "Ocorreu um erro ao importar os dados.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const renderUploadSection = (type: ImportType) => {
    const file = type === "mangaras" ? fileMangaras : fileColheitas;
    const uploading = type === "mangaras" ? uploadingMangaras : uploadingColheitas;
    const erros = type === "mangaras" ? errosMangaras : errosColheitas;
    const Icon = type === "mangaras" ? Scissors : Package;
    const nomeImportacao = type === "mangaras" ? "Mangarás" : "Colheitas";
    const colunas = type === "mangaras" 
      ? "Setor, Data Corte, Cor Fita, Quantidade" 
      : "Setor, Data Colheita, Cor Fita, Quantidade";

    return (
      <div className="space-y-6">
        {/* As colheitas entraram; só o rateio é que ficou para trás. Âmbar, não vermelho:
            nada se perdeu, mas o kg/cacho na tela de Vendas está velho até recalcular. */}
        {type === "colheitas" && rateioDesatualizado && (
          <Alert className="border-atencao/30 bg-atencao-suave" data-testid="aviso-rateio-desatualizado">
            <AlertCircle className="h-4 w-4 text-atencao" />
            <AlertTitle className="font-semibold text-atencao">Rateio não recalculado</AlertTitle>
            <AlertDescription className="text-atencao">
              As colheitas foram importadas normalmente, mas o rateio colheita ↔ venda não recalculou.
              O kg/cacho continua com os valores antigos até você rodar o recálculo em lote
              (botão <strong>Recalcular rateio</strong> no card Colhido, em Vendas).
            </AlertDescription>
          </Alert>
        )}
        {/* Error Display Section */}
        {erros.length > 0 && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertTitle className="font-semibold">
              {erros.length} {erros.length === 1 ? 'erro encontrado' : 'erros encontrados'} na importação
            </AlertTitle>
            <AlertDescription>
              <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">
                {erros.map((erro, index) => (
                  <div key={index} className="text-sm border-l-2 border-destructive pl-3 py-1">
                    <span className="font-medium">Linha {erro.linha}:</span> {erro.erro}
                  </div>
                ))}
              </div>
            </AlertDescription>
          </Alert>
        )}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl md:text-2xl font-semibold flex items-center gap-2">
              <Icon className="h-5 w-5 md:h-6 md:w-6" />
              Upload de {nomeImportacao}
            </CardTitle>
            <CardDescription>
              Faça upload de uma planilha Excel (.xlsx ou .xls) com dados de {nomeImportacao.toLowerCase()}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="border-2 border-dashed rounded-lg p-8 md:p-12 text-center hover-elevate transition-colors">
                <div className="flex flex-col items-center gap-4">
                  <div className="p-4 bg-primary/10 rounded-full">
                    <Upload className="h-8 w-8 md:h-12 md:w-12 text-primary" />
                  </div>
                  <div>
                    {/* `break-all`: nome de arquivo do celular é longo e sem
                        espaços — sem isso estourava a caixa em 390px. */}
                    <p className="font-medium text-base md:text-lg break-all">
                      {file ? file.name : `Selecione arquivo de ${nomeImportacao}`}
                    </p>
                    <p className="text-xs md:text-sm text-muted-foreground mt-1">
                      Ou arraste e solte aqui
                    </p>
                  </div>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => handleFileChange(type, e)}
                    className="hidden"
                    id={`file-upload-${type}`}
                    data-testid={`input-file-${type}`}
                  />
                  <label htmlFor={`file-upload-${type}`} className="w-full sm:w-auto">
                    <Button variant="outline" asChild className="w-full sm:w-auto min-h-11" data-testid={`button-select-file-${type}`}>
                      <span>Selecionar Arquivo</span>
                    </Button>
                  </label>
                </div>
              </div>

              {file && (
                <div className="flex justify-end">
                  <Button
                    onClick={() => handleUpload(type)}
                    disabled={uploading}
                    className="w-full sm:w-auto min-h-11"
                    data-testid={`button-upload-${type}`}
                  >
                    {uploading ? "Importando..." : `Importar ${nomeImportacao}`}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="h-5 w-5 md:h-6 md:w-6 text-primary" />
              <CardTitle className="text-lg md:text-xl">Formato da Planilha</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm md:text-base">Colunas esperadas:</p>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    {colunas}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm md:text-base">Formato de datas:</p>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    Use o formato DD/MM/YYYY (ex: 15/01/2025)
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm md:text-base">Importante:</p>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    Os setores e cores devem estar previamente cadastrados no sistema
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const passoAtualIndice = passo === "fim" ? PASSOS.length : PASSOS.findIndex(p => p.id === passo);

  return (
    <div className="space-y-6 md:space-y-8">
      <PageHeader titulo="Importar planilha" descricao="Importação de dados históricos em lote" />

      {/* ── Indicador de passos ──────────────────────────────────────────
          Estado por peso de tipo e pela pastilha do número, não por um
          componente de passo colorido à mão. Passo PENDENTE não é clicável:
          a ordem é do dado, não da tela. */}
      {/* `overflow-x-auto` como no PageTabs: com dois passos cabe em 390px
          medido, mas rótulo maior ou um terceiro passo rolam de lado em vez
          de vazar. */}
      <ol className="flex items-center gap-2 sm:gap-3 overflow-x-auto scrollbar-none" data-testid="passos-importacao">
        {PASSOS.map((p, i) => {
          const concluido = concluidos[p.id] === true;
          const atual = passo === p.id;
          const podeIr = concluido && !atual;
          const Icone = p.icone;
          return (
            <li key={p.id} className="flex items-center gap-2 sm:gap-3 min-w-0">
              <button
                type="button"
                disabled={!podeIr}
                onClick={() => podeIr && setPasso(p.id)}
                aria-current={atual ? "step" : undefined}
                data-testid={`passo-${p.id}`}
                className={cn(
                  "flex items-center gap-2 min-w-0 rounded-md px-1.5 py-1 transition-colors",
                  podeIr ? "cursor-pointer hover:bg-muted" : "cursor-default",
                )}
              >
                <span
                  className={cn(
                    "h-7 w-7 shrink-0 rounded-full inline-flex items-center justify-center text-xs font-bold",
                    // Concluído é a pastilha CLARA, não a verde sólida: medido,
                    // `bg-sucesso` (19,113,54) e `bg-primary` (18,130,59) são
                    // praticamente a mesma cor, e o indicador ficava sem
                    // distinguir "onde estou" de "o que já fiz". Sólido = atual;
                    // claro = feito; cinza = ainda não.
                    concluido
                      ? "bg-sucesso-suave text-sucesso"
                      : atual
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground",
                  )}
                >
                  {concluido ? <Check className="h-4 w-4" /> : p.n}
                </span>
                <span className="min-w-0 text-left">
                  <span
                    className={cn(
                      "block text-sm truncate",
                      atual ? "font-semibold text-foreground"
                        : concluido ? "font-medium text-foreground"
                          : "text-muted-foreground",
                    )}
                  >
                    <Icone className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
                    {p.label}
                  </span>
                  <span className="block text-[11px] text-muted-foreground">
                    {concluido ? "concluído" : atual ? "atual" : "pendente"}
                  </span>
                </span>
              </button>
              {i < PASSOS.length - 1 && (
                <span
                  aria-hidden
                  className={cn(
                    "h-px w-6 sm:w-12 shrink-0",
                    passoAtualIndice > i ? "bg-sucesso" : "bg-border",
                  )}
                />
              )}
            </li>
          );
        })}
      </ol>

      {/* O passo atual mostra só o SEU conteúdo — nunca as duas seções juntas. */}
      {passo !== "fim" && renderUploadSection(passo)}

      {/* Passo concluído com linha recusada: não avançou sozinho de propósito
          (o relatório de erro está logo acima). A saída é explícita. */}
      {passo !== "fim" && concluidos[passo] && (
        <div className="flex justify-end">
          <Button onClick={() => avancar(passo)} data-testid={`button-continuar-${passo}`}>
            {passo === "mangaras" ? "Continuar para colheitas" : "Concluir importação"}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      )}

      {/* Só o passo 1 pode ser pulado, e o motivo é preservar o que a tela já
          fazia: reimportar SÓ a planilha de colheitas era possível com as abas,
          e continua sendo. Pular não importa nada — só destrava o passo 2. */}
      {passo === "mangaras" && !concluidos.mangaras && (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            onClick={() => { setConcluidos(prev => ({ ...prev, mangaras: true })); setPasso("colheitas"); }}
            data-testid="button-pular-mangaras"
          >
            Já importei os mangarás — ir para colheitas
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      )}

      {/* ── Conclusão ────────────────────────────────────────────────── */}
      {passo === "fim" && (
        <Card data-testid="importacao-concluida">
          <CardHeader>
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-sucesso" />
              <CardTitle className="text-lg md:text-xl">Importação concluída</CardTitle>
            </div>
            <CardDescription>O que entrou nesta sessão de importação.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              {PASSOS.map(p => {
                const r = resultados[p.id];
                return (
                  <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2">
                    <span className="text-sm font-medium">{p.label}</span>
                    {r ? (
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-xs tabular-nums">
                          {r.sucesso} de {r.total} registros
                        </span>
                        {r.erros > 0
                          ? <StatusBadge tom="atencao">{r.erros} {r.erros === 1 ? "linha recusada" : "linhas recusadas"}</StatusBadge>
                          : <StatusBadge tom="sucesso" ponto>sem erros</StatusBadge>}
                      </span>
                    ) : (
                      <StatusBadge tom="neutro">não importado nesta sessão</StatusBadge>
                    )}
                  </div>
                );
              })}
            </div>

            {rateioDesatualizado && (
              <Alert className="border-atencao/30 bg-atencao-suave">
                <AlertCircle className="h-4 w-4 text-atencao" />
                <AlertTitle className="font-semibold text-atencao">Rateio não recalculado</AlertTitle>
                <AlertDescription className="text-atencao">
                  As colheitas entraram, mas o rateio colheita ↔ venda não recalculou. O kg/cacho
                  continua com os valores antigos até rodar o recálculo em lote
                  (botão <strong>Recalcular rateio</strong> no card Colhido, em Vendas).
                </AlertDescription>
              </Alert>
            )}

            <div className="flex flex-col sm:flex-row gap-2">
              <Button asChild variant="outline" className="min-h-11">
                <Link href="/cacho/mangaras" data-testid="link-ver-mangaras">Ver mangarás</Link>
              </Button>
              <Button asChild variant="outline" className="min-h-11">
                <Link href="/cacho/colheitas" data-testid="link-ver-colheitas">Ver colheitas</Link>
              </Button>
              <Button
                variant="ghost"
                className="min-h-11 sm:ml-auto"
                onClick={() => { setPasso("mangaras"); setConcluidos({}); setResultados({}); }}
                data-testid="button-nova-importacao"
              >
                Importar outra planilha
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
